/**
 * "Which of my locations have active bookings" — the live active-booking count
 * split out per location.
 *
 * `activeBookings` answers *how many*; this answers *where*. Before it
 * existed, "which locations have active bookings" and "do any of my locations
 * have active bookings" both matched the counting skill on the words "active
 * bookings" and came back as a bare account total — an answer to a different
 * question, repeated verbatim however the user rephrased it.
 *
 * The data comes from the same supplier bookings search the count uses, so the
 * two skills always agree. The difference is that the rows are pulled and
 * grouped client-side: the endpoint can only count per location or per account,
 * and counting per location would cost one request for every location the
 * supplier has. Pulling rows is one request for most suppliers and never more
 * than {@link MAX_ACTIVE_BOOKING_PAGES}.
 */

import { countActiveBookings } from '../location/requests/location-requests';
import { tokenize, tokensSimilar } from './fuzzyMatch';
import {
  fetchActiveSupplierBookings,
  MAX_ACTIVE_BOOKING_PAGES,
  type ActiveBookingRow,
} from './requests/active-bookings-requests';
import type { LocationCandidate } from './locationResolver';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';

const MY_BOOKINGS_LINK: KnowledgeLink = { label: 'Open My Bookings', path: '/supplier-bookings' };

/**
 * Most locations listed in the breakdown. Each row carries its own link, so a
 * large portfolio would otherwise push the answer text off the top of the panel.
 * The busiest are kept, since those are the ones a supplier is asking about.
 */
const MAX_ROWS = 20;

/** Most booking-free locations named one by one before they are only counted. */
const MAX_NAMED_EMPTY = 5;

const LOCATION_NOUNS = [
  'location',
  'locations',
  'yard',
  'yards',
  'site',
  'sites',
  'facility',
  'facilities',
];

/** Words that turn "how many bookings" into "how many at each location". */
const BREAKDOWN_CUES = ['which', 'any', 'each', 'every', 'per', 'by', 'breakdown', 'split'];

const pluralize = (count: number, singular: string): string =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

/**
 * Typo tolerance only for long words. At three or four letters a single edit is
 * a different word — "any" is one away from "and" and "many", "site" from
 * "size" — and either would turn an ordinary total into a breakdown.
 */
const wordMatches = (token: string, word: string): boolean =>
  word.length >= 7 ? tokensSimilar(token, word) : token === word;

/**
 * True when a bookings question asks *which* locations rather than *how many*
 * bookings: "how many bookings do I have at each location", "do any of my yards
 * have active bookings".
 *
 * Needs both a cue and a location noun, so "do I have any active bookings" is
 * still answered as a total.
 */
export const asksForLocationBreakdown = (message: string): boolean => {
  if (!message) return false;
  const tokens = tokenize(message);
  const hasCue = tokens.some((token) => BREAKDOWN_CUES.some((cue) => wordMatches(token, cue)));
  const hasNoun = tokens.some((token) => LOCATION_NOUNS.some((noun) => wordMatches(token, noun)));
  return hasCue && hasNoun;
};

/** Deep link to one location's active bookings, as the location cards use. */
export const locationBookingsPath = (locationId: string): string =>
  `/bookings/supplier-bookings?status=ACTIVE&locationId=${encodeURIComponent(locationId)}`;

interface LocationTally {
  /** Null only when a booking row arrived without a location id. */
  id: string | null;
  name: string;
  count: number;
}

/**
 * Counts bookings per location. Names come from the export where possible, so
 * the breakdown uses the same names the rest of the conversation does, and fall
 * back to the booking's own copy for a location the export does not list.
 */
const tallyByLocation = (
  rows: ActiveBookingRow[],
  nameFor: Map<string, string>,
): LocationTally[] => {
  const tallies = new Map<string, LocationTally>();

  rows.forEach((row) => {
    const id = row.locationId ?? null;
    const key = id ?? `name:${row.locationName ?? ''}`;
    const existing = tallies.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }
    tallies.set(key, {
      id,
      name: (id && nameFor.get(id)) || row.locationName || 'Unnamed location',
      count: 1,
    });
  });

  return Array.from(tallies.values());
};

/** "A", "A and B", "A, B and C". */
const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Who the answer is about: "your" for the account or a status scope, "those"
 * for a named set the user picked out themselves — plus the status word, if
 * any, so a scoped answer reads "2 of your 3 active locations".
 */
const ownerPhrase = (context: KnowledgeContext): { owner: string; qualifier: string } => {
  if (context.scope.kind === 'STATUS') return { owner: 'your', qualifier: `${context.scope.word} ` };
  return { owner: context.wholeAccount ? 'your' : 'those', qualifier: '' };
};

const noneAnswer = (context: KnowledgeContext): KnowledgeAnswer => {
  const { owner, qualifier } = ownerPhrase(context);
  const group = owner === 'those' ? `those ${context.targets.length}` : owner;
  return {
    text: `None of ${group} ${qualifier}locations have active bookings right now.`,
    link: MY_BOOKINGS_LINK,
  };
};

/** A single named location: an exact count, no sampling involved. */
const singleLocationAnswer = async (target: LocationCandidate): Promise<KnowledgeAnswer> => {
  const response = await countActiveBookings(target.id);
  const count: number = response?.body?.count ?? 0;

  if (count === 0) {
    return { text: `**${target.name}** has no active bookings right now.`, link: MY_BOOKINGS_LINK };
  }

  return {
    text: `**${target.name}** has ${pluralize(count, 'active booking')} right now.`,
    link: { label: 'View these bookings', path: locationBookingsPath(target.id) },
  };
};

/** The sentence naming (or counting) the locations with nothing booked. */
const idleSentence = (idle: string[]): string => {
  if (idle.length === 0) return '';
  if (idle.length > MAX_NAMED_EMPTY) return ` The other ${idle.length} have none.`;
  const names = joinNames(idle.map((name) => `**${name}**`));
  return ` ${names} ${idle.length === 1 ? 'has' : 'have'} none.`;
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { targets, wholeAccount } = context;

  if (!wholeAccount && targets.length === 1) return singleLocationAnswer(targets[0]);

  const sample = await fetchActiveSupplierBookings();
  const nameFor = new Map(targets.map((target) => [target.id, target.name]));

  // The whole account keeps every location a booking names, even one missing
  // from the export — it is still the supplier's booking. A narrowed scope keeps
  // only the locations it selected.
  const busy = tallyByLocation(sample.rows, nameFor)
    .filter((tally) => wholeAccount || (tally.id !== null && nameFor.has(tally.id)))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const truncated = sample.rows.length < sample.total;
  if (busy.length === 0 && !truncated) return noneAnswer(context);

  const busyIds = new Set(busy.map((tally) => tally.id));
  const idle = targets.filter((target) => !busyIds.has(target.id)).map((target) => target.name);
  const considered = busy.length + idle.length;
  const total = busy.reduce((sum, tally) => sum + tally.count, 0);

  const rows: KnowledgeBreakdownRow[] = busy.slice(0, MAX_ROWS).map((tally) => ({
    label: tally.name,
    value: String(tally.count),
    link: tally.id ? { label: 'View bookings', path: locationBookingsPath(tally.id) } : undefined,
  }));
  const overflowNote =
    busy.length > MAX_ROWS ? ` The ${MAX_ROWS} busiest are listed below.` : '';

  if (truncated) {
    // Past the page cap some bookings were never seen, so a location absent
    // from the split cannot be called empty. Say what was covered instead.
    return {
      text:
        `You have **${pluralize(sample.total, 'active booking')}** right now — more than I ` +
        `can group in one answer, so this split covers the first ${sample.rows.length} ` +
        `(${MAX_ACTIVE_BOOKING_PAGES} pages). A location missing from it may still have ` +
        `some.${overflowNote}`,
      breakdown: rows,
      link: MY_BOOKINGS_LINK,
    };
  }

  if (considered === 1) {
    const [only] = busy;
    return {
      text: `**${only.name}** has ${pluralize(only.count, 'active booking')} right now.`,
      link: only.id
        ? { label: 'View these bookings', path: locationBookingsPath(only.id) }
        : MY_BOOKINGS_LINK,
    };
  }

  const { owner, qualifier } = ownerPhrase(context);
  const totalPhrase = `${pluralize(total, 'active booking')} in total`;
  let lead: string;
  if (idle.length === 0) {
    lead =
      considered === 2
        ? `Both of ${owner} ${qualifier}locations have active bookings right now, ${totalPhrase}.`
        : `All ${considered} of ${owner} ${qualifier}locations have active bookings right now, ${totalPhrase}.`;
  } else {
    lead =
      `**${busy.length} of ${owner} ${considered} ${qualifier}locations** ` +
      `${busy.length === 1 ? 'has' : 'have'} active bookings right now, ${totalPhrase}.`;
  }

  return {
    text: `${lead}${idleSentence(idle)}${overflowNote}`,
    breakdown: rows,
    link: MY_BOOKINGS_LINK,
  };
};

/**
 * Supplier-only, like the count it splits: buyers have no locations to break
 * bookings down by.
 */
export const activeBookingsByLocationQuery: KnowledgeQuery = {
  id: 'activeBookingsByLocation',
  audiences: ['supplier'],
  // Each is longer than the counting skill's "active bookings" / "how many
  // bookings", so these win under longest-match. Phrasings that open with the
  // counting skill's words ("how many bookings do I have at each location") are
  // handed over by that skill instead — see asksForLocationBreakdown.
  synonyms: [
    'locations have active bookings',
    'location has active bookings',
    'locations with active bookings',
    'locations have bookings',
    'location has bookings',
    'locations with bookings',
    'yards have active bookings',
    'sites have active bookings',
    'most active bookings',
    'active bookings by location',
    'bookings by location',
    'active bookings per location',
    'bookings per location',
    'active bookings at each location',
    'bookings at each location',
    'bookings for each location',
    'bookings at each of my locations',
  ],
  // Rescue for "which of my yards currently have bookings". The live-word group
  // keeps it off "which locations have pending bookings" and "which location had
  // the most bookings last month", neither of which this skill answers.
  requires: [
    ['which', 'each', 'every', 'per', 'breakdown'],
    LOCATION_NOUNS,
    ['booking', 'bookings'],
    ['active', 'current', 'currently', 'live', 'ongoing'],
  ],
  example: 'which of my locations have active bookings',
  description:
    'Which of the supplier\u2019s locations have bookings active right now, and how many ' +
    'each has \u2014 the live active-booking count split per location, busiest first, ' +
    'naming the locations that have none. Use when the question asks which, any or ' +
    'each location rather than for one overall number. A live snapshot, so it ignores ' +
    'any time period.',
  answer,
};

export const __testing = { tallyByLocation, joinNames, MAX_ROWS, MAX_NAMED_EMPTY };
